import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Space,
  Button,
  Avatar,
  message,
  Spin,
  Tooltip,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  DollarOutlined,
  LinkOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';


const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  padding: 24px;
  background: ${COLORS.bgPrimary};
  min-height: 100vh;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  color: ${COLORS.textPrimary} !important;
  margin-bottom: 24px;

  .ant-card-head {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
  }

  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }

  .ant-card-body {
    background: ${COLORS.bgSecondary} !important;
  }

  .ant-descriptions {
    background: ${COLORS.bgSecondary} !important;
  }

  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }

  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }

  .ant-descriptions-row {
    border-color: ${COLORS.border} !important;
  }
`;

const getExplorerTxUrl = (explorerUrl: string | undefined, txHash: string, blockchainType?: string): string | null => {
  if (!explorerUrl || !txHash) return null;
  const base = explorerUrl.startsWith('http') ? explorerUrl : `https://${explorerUrl}`;
  const cleanBase = base.replace(/\/$/, '');
  switch ((blockchainType || 'EVM').toUpperCase()) {
    case 'TRON': return `${cleanBase}/#/transaction/${txHash}`;
    default: return `${cleanBase}/tx/${txHash}`;
  }
};

const TxHashLink: React.FC<{ hash: string | undefined; explorerUrl: string | undefined; blockchainType?: string }> = ({ hash, explorerUrl, blockchainType }) => {
  if (!hash) return <span>-</span>;
  const url = getExplorerTxUrl(explorerUrl, hash, blockchainType);
  const display = `${hash.slice(0, 10)}...${hash.slice(-8)}`;
  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'monospace', color: COLORS.accent, textDecoration: 'none' }}>
        {display} <LinkOutlined style={{ fontSize: 12, marginLeft: 4 }} />
      </a>
    );
  }
  return <span style={{ fontFamily: 'monospace' }}>{display}</span>;
};

const MerchantSweepDetails: React.FC = () => {
  const { sweepId } = useParams<{ sweepId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sweepData, setSweepData] = useState<any>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [chains, setChains] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [wallets, setWallets] = useState<any[]>([]);
  const [loadingChains, setLoadingChains] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [loadingWallets, setLoadingWallets] = useState(false);

  const fetchSweepDetails = useCallback(async () => {
    if (!sweepId) return;
    try {
      setLoading(true);
      const endpoint = (API_ENDPOINTS.MERCHANT_GET_SWEEP_LIST_ITEM || '/api/Merchant/lobby/sweep-list/{id}').replace('{id}', sweepId);
      const response = await apiClient.get(endpoint);
      if (response.data?.status === 'success' && response.data?.data) {
        setSweepData(response.data.data);
      }
    } catch (error: any) {
      message.error('Failed to load sweep details');
      console.error('Error fetching sweep details:', error);
    } finally {
      setLoading(false);
    }
  }, [sweepId]);

  useEffect(() => {
    fetchSweepDetails();
  }, [fetchSweepDetails]);

  const fetchChains = useCallback(async () => {
    try {
      setLoadingChains(true);
      const response = await apiClient.get(API_ENDPOINTS.PUBLIC_GET_BLOCKCHAIN_NETWORKS || '/api/Public/blockchain-networks', {
        params: { pageId: 1, perPage: 100 }
      });
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setChains(data.itemsList || data.items || (Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching chains:', error);
    } finally {
      setLoadingChains(false);
    }
  }, []);

  const fetchCurrencies = useCallback(async () => {
    try {
      setLoadingCurrencies(true);
      const response = await apiClient.get(API_ENDPOINTS.PUBLIC_GET_CURRENCIES || '/api/Public/currencies', {
        params: { pageId: 1, perPage: 100 }
      });
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setCurrencies(data.itemsList || data.items || (Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
    } finally {
      setLoadingCurrencies(false);
    }
  }, []);

  const fetchWallets = useCallback(async () => {
    try {
      setLoadingWallets(true);
      const response = await apiClient.get('/api/Merchant/lobby/wallets', {
        params: { pageId: 1, perPage: 100 }
      });
      if (response.data?.status === 'success' && response.data?.data) {
        setWallets(response.data.data.itemsList || []);
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
    } finally {
      setLoadingWallets(false);
    }
  }, []);

  useEffect(() => {
    fetchChains();
    fetchCurrencies();
    fetchWallets();
  }, [fetchChains, fetchCurrencies, fetchWallets]);

  const handleEdit = () => {
    if (!sweepData) return;
    form.setFieldsValue({
      walletId: sweepData.wallet?.walletId,
      chainId: sweepData.chainId,
      currencyId: sweepData.currencyId,
      currencySymbol: sweepData.currencySymbol,
      amount: Number(sweepData.amount),
      transactionHash: sweepData.transactionHash,
      transactionBlock: sweepData.transactionBlock ? Number(sweepData.transactionBlock) : undefined,
      sweepStatus: sweepData.sweepStatus,
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values: any) => {
    if (!sweepId) return;
    try {
      await apiClient.put(
        (API_ENDPOINTS.MERCHANT_UPDATE_SWEEP_LIST_ITEM || '/api/Merchant/lobby/sweep-list/{id}/update').replace('{id}', sweepId),
        values
      );
      message.success('Sweep list item updated successfully');
      setEditModalVisible(false);
      form.resetFields();
      fetchSweepDetails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update sweep list item');
    }
  };

  const handleDelete = async () => {
    if (!sweepId) return;
    try {
      await apiClient.delete(
        (API_ENDPOINTS.MERCHANT_DELETE_SWEEP_LIST_ITEM || '/api/Merchant/lobby/sweep-list/{id}/delete').replace('{id}', sweepId)
      );
      message.success('Sweep list item deleted successfully');
      navigate('/igwena/sweep-list');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete sweep list item');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: '100px' }} />
      </PageContainer>
    );
  }

  if (!sweepData) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/igwena/sweep-list')}>
            Back to Sweep List
          </Button>
        </HeaderContainer>
        <StyledCard>
          <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
            Sweep not found
          </div>
        </StyledCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/igwena/sweep-list')}>
          Back to Sweep List
        </Button>
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this item?"
            onConfirm={handleDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      </HeaderContainer>

      <StyledCard title="Sweep Details">
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Sweep ID">
            <span style={{ fontFamily: 'monospace' }}>{sweepData.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={sweepData.sweepStatus === 'COMPLETED' ? 'green' : sweepData.sweepStatus === 'FAILED' ? 'red' : 'orange'}>
              {sweepData.sweepStatus}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Wallet ID">
            <Space>
              <span 
                style={{ 
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  color: COLORS.accent,
                  textDecoration: 'underline'
                }}
                onClick={() => {
                  if (sweepData.wallet?.id) {
                    navigate(`/igwena/wallets/${sweepData.wallet.id}`);
                  }
                }}
              >
                {sweepData.wallet?.walletId || '-'}
              </span>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Wallet Address">
            <span style={{ fontFamily: 'monospace' }}>
              {sweepData.blockchainNetwork?.blockchainType?.toUpperCase() === 'BITCOIN'
                ? sweepData.wallet?.bitcoinAddress
                : sweepData.blockchainNetwork?.blockchainType?.toUpperCase() === 'SOLANA'
                ? sweepData.wallet?.solanaAddress
                : sweepData.blockchainNetwork?.blockchainType?.toUpperCase() === 'TRON'
                ? sweepData.wallet?.tronAddress || sweepData.wallet?.evmAddress
                : sweepData.wallet?.evmAddress || '-'}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Blockchain Network">
            <Space>
              {sweepData.blockchainNetwork?.iconUrl && (
                <Avatar
                  src={sweepData.blockchainNetwork.iconUrl}
                  icon={<GlobalOutlined />}
                  size={24}
                  style={{ 
                    backgroundColor: COLORS.bgSecondary, 
                    color: COLORS.textPrimary, 
                    border: `1px solid ${COLORS.border}` 
                  }}
                />
              )}
              <span>
                {sweepData.blockchainNetwork?.name || '-'}
                {sweepData.blockchainNetwork?.networkId != null && sweepData.blockchainNetwork.networkId !== 0 && ` [${sweepData.blockchainNetwork.networkId}]`}
              </span>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Currency">
            <Space>
              {sweepData.currency?.iconUrl && (
                <Avatar
                  src={sweepData.currency.iconUrl}
                  icon={<DollarOutlined />}
                  size={24}
                  style={{ 
                    backgroundColor: COLORS.bgSecondary, 
                    color: COLORS.textPrimary, 
                    border: `1px solid ${COLORS.border}` 
                  }}
                />
              )}
              <span>
                {sweepData.currencySymbol || '-'} {sweepData.currency?.name && `(${sweepData.currency.name})`}
              </span>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Amount">
            <span>
              {Number(sweepData.amount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 8,
              })} {sweepData.currencySymbol}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label={<span>Incoming Tx Hash <Tooltip title="The on-chain transaction hash of the incoming customer payment detected by OnchainWatcher"><InfoCircleOutlined style={{ fontSize: 12, color: '#94A3B8', marginLeft: 4 }} /></Tooltip></span>}>
            <TxHashLink hash={sweepData.transactionHash} explorerUrl={sweepData.blockchainNetwork?.explorerUrl} blockchainType={sweepData.blockchainNetwork?.blockchainType} />
          </Descriptions.Item>
          <Descriptions.Item label="Transaction Block">
            {sweepData.transactionBlock ? Number(sweepData.transactionBlock).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={<span>Sweep Tx Hash <Tooltip title="The on-chain transaction hash of the outgoing sweep that moved funds from the customer wallet to the custodial wallet"><InfoCircleOutlined style={{ fontSize: 12, color: '#94A3B8', marginLeft: 4 }} /></Tooltip></span>}>
            <TxHashLink hash={sweepData.sweepTransactionHash} explorerUrl={sweepData.blockchainNetwork?.explorerUrl} blockchainType={sweepData.blockchainNetwork?.blockchainType} />
          </Descriptions.Item>
          <Descriptions.Item label="Sweep Amount">
            {sweepData.sweepAmount ? (
              <span>
                {Number(sweepData.sweepAmount).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })} {sweepData.currencySymbol}
              </span>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Retry Count">
            <Tag color={sweepData.retryCount > 0 ? 'orange' : 'default'}>
              {sweepData.retryCount ?? 0}
            </Tag>
          </Descriptions.Item>
          {sweepData.discoveryPeriodSeconds != null ? (
            <Descriptions.Item label="Discovery Period">
              <span title={`Time between block timestamp and discovery: ${sweepData.discoveryPeriodSeconds} seconds`}>
                {sweepData.discoveryPeriodSeconds < 60
                  ? `${sweepData.discoveryPeriodSeconds}s`
                  : sweepData.discoveryPeriodSeconds < 3600
                  ? `${Math.floor(sweepData.discoveryPeriodSeconds / 60)}m ${sweepData.discoveryPeriodSeconds % 60}s`
                  : `${Math.floor(sweepData.discoveryPeriodSeconds / 3600)}h ${Math.floor((sweepData.discoveryPeriodSeconds % 3600) / 60)}m`}
              </span>
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label="Sweep Requester">
            <Tag>{sweepData.sweepRequester || '-'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Fail Reason">
            {sweepData.sweepFailReason ? (
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                padding: '12px',
                backgroundColor: COLORS.bgPrimary,
                borderRadius: '4px',
                border: `1px solid ${COLORS.border}`,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: COLORS.textPrimary
              }}>
                {sweepData.sweepFailReason}
              </div>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Sweep Comment">
            {sweepData.sweepComment || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {sweepData.createdAt ? dayjs(sweepData.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {sweepData.updatedAt ? dayjs(sweepData.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <Modal
        title="Edit Sweep List Item"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
        okText="Update"
        cancelText="Cancel"
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            name="walletId"
            label="Wallet ID"
            rules={[{ required: true, message: 'Please enter wallet ID' }]}
          >
            <Select
              placeholder="Select wallet"
              showSearch
              loading={loadingWallets}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={wallets.map(w => ({ value: w.walletId, label: w.walletId }))}
            />
          </Form.Item>
          <Form.Item
            name="chainId"
            label="Blockchain Network"
            rules={[{ required: true, message: 'Please select a blockchain network' }]}
          >
            <Select
              placeholder="Select blockchain network"
              showSearch
              loading={loadingChains}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={chains.map(c => ({
                value: c.id,
                label: c.networkId ? `${c.name} [${c.networkId}]` : c.name
              }))}
            />
          </Form.Item>
          <Form.Item
            name="currencyId"
            label="Currency"
            rules={[{ required: true, message: 'Please select a currency' }]}
          >
            <Select
              placeholder="Select currency"
              showSearch
              loading={loadingCurrencies}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={currencies.map(c => ({ value: c.id, label: `${c.symbol} - ${c.name}` }))}
            />
          </Form.Item>
          <Form.Item
            name="currencySymbol"
            label="Currency Symbol"
            rules={[{ required: true, message: 'Please enter currency symbol' }]}
          >
            <Input placeholder="e.g., ETH, BTC, USDT" />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: 'Please enter amount' }]}
          >
            <InputNumber
              placeholder="Enter amount"
              style={{ width: '100%' }}
              min={0}
              step={0.00000001}
              precision={18}
            />
          </Form.Item>
          <Form.Item
            name="transactionHash"
            label="Transaction Hash"
            rules={[{ required: true, message: 'Please enter transaction hash' }]}
          >
            <Input placeholder="Enter transaction hash" />
          </Form.Item>
          <Form.Item
            name="transactionBlock"
            label="Transaction Block (Optional)"
          >
            <InputNumber
              placeholder="Enter block number"
              style={{ width: '100%' }}
              min={0}
            />
          </Form.Item>
          <Form.Item
            name="sweepStatus"
            label="Sweep Status"
          >
            <Select>
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="COMPLETED">Completed</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default MerchantSweepDetails;

